import { pinnedContracts } from '@hoodmorning/config';
import { SELECTED_ACCOUNT_CHAIN_ID, bytes32Schema, nonzeroAddressSchema, type Address, type Hex, type PreparedOperation, type TradeIntent } from '@hoodmorning/contracts';
import {
  decodeEventLog,
  decodeFunctionData,
  encodeAbiParameters,
  encodeEventTopics,
  getAddress,
  isAddressEqual,
  keccak256,
  parseAbi,
  parseAbiParameters,
  toEventSelector,
  verifyMessage,
  zeroAddress,
  type PublicClient,
  type TransactionReceipt,
} from 'viem';
import {
  entryPoint07Abi,
  getUserOperationHash,
  toUserOperation,
  type UserOperation,
  type UserOperationReceipt,
} from 'viem/account-abstraction';
import { canonicalSimulationData } from './batchSimulation.js';
import { hashPreparedCalls } from './calls.js';
import { observeExternalRoutePools } from './externalRoutePools.js';
import { assertSmaBOwnerNonce, isValidNonceLane } from './nonce.js';
import { extractSelectedOwnerOperationExecutionLogs } from './operationExecutionLogs.js';
import { readTerminalQuoteHistory, readTerminalQuoteRecord, isNativeTerminalQuoteRecord } from './terminalQuoteRecord.js';
import { reconcileOwnerOperationReceipt } from './userOperationReceipt.js';
import { validateUserOperationBudget, type UserOperationBudget } from './userOperation.js';
import { observeVaultIdentityAtBlock } from './vaultIdentityObservation.js';
import type { VaultAddressIdentity } from './vault.js';
import { createSmaBVaultIntent } from './vault.js';
import { createHistoricalCanonicalBlockReadClient, type CanonicalReadPort } from './canonicalBlockReadClient.js';
import { readNativeWrapperObservationProfile, assertNativeWrapperRouterCompatibility, type NativeWrapperObservationProfile } from './nativeWrapperObservation.js';
import { collectSelectedNativeTerminalSettlementTrace, type DirectStorageTokenProfile, type LocalTracePort } from './externalTerminalSettlementTrace.js';

const transferAbi = parseAbi(['event Transfer(address indexed from,address indexed to,uint256 value)']);
const v2SwapAbi = parseAbi(['event Swap(address indexed sender,uint256 amount0In,uint256 amount1In,uint256 amount0Out,uint256 amount1Out,address indexed to)']);
const v3SwapAbi = parseAbi(['event Swap(address indexed sender,address indexed recipient,int256 amount0,int256 amount1,uint160 sqrtPriceX96,uint128 liquidity,int24 tick)']);
const transferTopic = toEventSelector(transferAbi[0]);
const v2SwapTopic = toEventSelector(v2SwapAbi[0]);
const v3SwapTopic = toEventSelector(v3SwapAbi[0]);
type ReviewedPolicy = Omit<Parameters<typeof readTerminalQuoteRecord>[1]['policy'], 'nowUnixSeconds'>;
type ReceiptClient = Pick<PublicClient,
  'getChainId' | 'getBlock' | 'getBytecode' | 'getStorageAt' | 'readContract' | 'getTransaction' | 'getTransactionReceipt'>;

export type ExternalTradeReceiptInput = {
  client: ReceiptClient;
  record: unknown;
  intent: TradeIntent<4663 | 46630>;
  reviewed: {
    policy: ReviewedPolicy;
    profile: Parameters<typeof observeExternalRoutePools>[0]['profile'];
    permit2: { address: Address; codeHash: Hex };
  };
  identity: VaultAddressIdentity<4663 | 46630>;
  operation: PreparedOperation;
  owner: Address;
  /** Reviewed unsigned wire fields. The actual signature is recovered only from handleOps. */
  userOperation: UserOperation<'0.7'>;
  expectedHash: Hex;
  budget: UserOperationBudget;
  reported: UserOperationReceipt<'0.7'> | null;
  requiredConfirmations?: bigint;
};

const same = (left: string | null | undefined, right: string) => typeof left === 'string' && left.toLowerCase() === right.toLowerCase();
const hexEqual = (left: unknown, right: unknown) => typeof left === 'string' && typeof right === 'string' && left.toLowerCase() === right.toLowerCase();

function dateSeconds(value: string, label: string) {
  const milliseconds = Date.parse(value);
  if (!Number.isFinite(milliseconds) || milliseconds < 0) throw new Error(`Invalid ${label}`);
  return BigInt(Math.floor(milliseconds / 1_000));
}

function assertInitialCommitment(input: {
  history: ReturnType<typeof readTerminalQuoteHistory>;
  intent: TradeIntent<4663 | 46630>;
  policy: ReviewedPolicy;
  operation: PreparedOperation;
  owner: Address;
  userOperation: UserOperation<'0.7'>;
  expectedHash: Hex;
  budget: UserOperationBudget;
}) {
  const { history, intent, policy, operation, userOperation, budget } = input;
  const owner = getAddress(nonzeroAddressSchema.parse(input.owner));
  bytes32Schema.parse(input.expectedHash);
  const quote = history.record.quote;
  if (intent.chainId !== SELECTED_ACCOUNT_CHAIN_ID || quote.intentId !== intent.intentId || quote.amountIn !== intent.amountIn
    || !isAddressEqual(getAddress(quote.router), policy.router)
    || quote.hoodMorningFeeBps !== policy.requiredFeeBps || !isAddressEqual(getAddress(quote.feeRecipient), policy.feeRecipient)) {
    throw new Error('Historical external quote/intent binding mismatch');
  }
  if (!operation.operationId || operation.operationId.length > 256 || !isValidNonceLane(operation.nonceKey)
    || !isAddressEqual(operation.vault, getAddress(intent.vault)) || !isAddressEqual(operation.vault, getAddress(policy.vault))
    || history.record.callsHash.toLowerCase() !== operation.callsHash.toLowerCase()
    || hashPreparedCalls(operation.calls).toLowerCase() !== operation.callsHash.toLowerCase()
    || operation.calls.length !== history.record.calls.length
    || operation.calls.some((call, index) => {
      const saved = history.record.calls[index]!;
      return !isAddressEqual(call.to, getAddress(saved.to)) || call.value !== BigInt(saved.value) || call.data.toLowerCase() !== saved.data.toLowerCase();
    })) throw new Error('External trade operation commitment mismatch');
  if (!isAddressEqual(userOperation.sender, operation.vault)
    || userOperation.callData.toLowerCase() !== canonicalSimulationData(operation, userOperation.callData).toLowerCase()) {
    throw new Error('External trade user operation differs from reviewed calls');
  }
  assertSmaBOwnerNonce(userOperation.nonce, operation.nonceKey);
  validateUserOperationBudget(userOperation, budget);
  const expected = getUserOperationHash({ userOperation, chainId: SELECTED_ACCOUNT_CHAIN_ID,
    entryPointAddress: pinnedContracts.entryPointV07.address, entryPointVersion: '0.7' });
  if (!same(expected, input.expectedHash) || owner === getAddress(operation.vault)) throw new Error('External trade owner/hash mismatch');
  return owner;
}

function assertActualSignedOperation(input: {
  transactionInput: Hex;
  reviewed: UserOperation<'0.7'>;
  expectedHash: Hex;
  owner: Address;
}) {
  const decoded = decodeFunctionData({ abi: entryPoint07Abi, data: input.transactionInput });
  if (decoded.functionName !== 'handleOps') throw new Error('Direct EntryPoint handleOps required');
  // ox unpacks correctly at runtime, while its generic preserves the packed input shape.
  const actual = decoded.args[0].map(item => toUserOperation(item) as unknown as UserOperation<'0.7'>);
  const hashes = actual.map(item => getUserOperationHash({ userOperation: item, chainId: SELECTED_ACCOUNT_CHAIN_ID,
    entryPointAddress: pinnedContracts.entryPointV07.address, entryPointVersion: '0.7' }));
  const matches = hashes.map((hash, index) => same(hash, input.expectedHash) ? index : -1).filter(index => index >= 0);
  if (matches.length !== 1) throw new Error('Exact selected signed operation required');
  const selected = actual[matches[0]!]!;
  const reviewed = input.reviewed;
  const fields = ['sender', 'nonce', 'callData', 'callGasLimit', 'verificationGasLimit', 'preVerificationGas',
    'maxFeePerGas', 'maxPriorityFeePerGas', 'factory', 'factoryData', 'paymaster', 'paymasterData',
    'paymasterVerificationGasLimit', 'paymasterPostOpGasLimit'] as const;
  for (const field of fields) {
    const expected = reviewed[field], observed = selected[field];
    if (typeof expected === 'string' || typeof observed === 'string') {
      if (!hexEqual(expected, observed)) throw new Error('Actual signed operation fields changed');
    } else if (expected !== observed) throw new Error('Actual signed operation fields changed');
  }
  if (!/^0xff00[0-9a-f]{130}$/i.test(selected.signature)) throw new Error('Unexpected historical SMA-B signature');
  return verifyMessage({ address: input.owner, message: { raw: input.expectedHash }, signature: `0x${selected.signature.slice(6)}` })
    .then(valid => { if (!valid) throw new Error('Historical owner signature mismatch'); });
}

function exactTopics(observed: readonly unknown[], expected: readonly unknown[]) {
  if (observed.length !== expected.length || expected.some((topic, index) => !hexEqual(topic, observed[index]))) {
    throw new Error('Noncanonical external trade event');
  }
}

function canonicalTransfer(log: TransactionReceipt['logs'][number]) {
  const args = decodeEventLog({ abi: transferAbi, eventName: 'Transfer', topics: log.topics, data: log.data, strict: true }).args;
  exactTopics(log.topics, encodeEventTopics({ abi: transferAbi, eventName: 'Transfer', args: { from: args.from, to: args.to } }));
  if (!hexEqual(log.data, encodeAbiParameters(parseAbiParameters('uint256'), [args.value]))) throw new Error('Noncanonical external trade event');
  return args;
}

function canonicalV2Swap(log: TransactionReceipt['logs'][number]) {
  const args = decodeEventLog({ abi: v2SwapAbi, eventName: 'Swap', topics: log.topics, data: log.data, strict: true }).args;
  exactTopics(log.topics, encodeEventTopics({ abi: v2SwapAbi, eventName: 'Swap', args: { sender: args.sender, to: args.to } }));
  if (!hexEqual(log.data, encodeAbiParameters(parseAbiParameters('uint256,uint256,uint256,uint256'),
    [args.amount0In, args.amount1In, args.amount0Out, args.amount1Out]))) throw new Error('Noncanonical external trade event');
  return args;
}

function canonicalV3Swap(log: TransactionReceipt['logs'][number]) {
  const args = decodeEventLog({ abi: v3SwapAbi, eventName: 'Swap', topics: log.topics, data: log.data, strict: true }).args;
  exactTopics(log.topics, encodeEventTopics({ abi: v3SwapAbi, eventName: 'Swap', args: { sender: args.sender, recipient: args.recipient } }));
  if (!hexEqual(log.data, encodeAbiParameters(parseAbiParameters('int256,int256,uint160,uint128,int24'),
    [args.amount0, args.amount1, args.sqrtPriceX96, args.liquidity, args.tick]))) throw new Error('Noncanonical external trade event');
  return args;
}

function transferLogs(logs: TransactionReceipt['logs'], token: Address) {
  return logs.filter(log => isAddressEqual(log.address, token) && hexEqual(log.topics[0], transferTopic)).map(log => {
    if (log.topics.length !== 3 || !/^0x[0-9a-f]{64}$/i.test(log.data)
      || !log.topics.slice(1).every(topic => /^0x0{24}[0-9a-f]{40}$/i.test(topic))) throw new Error('Malformed ERC20 Transfer');
    const args = canonicalTransfer(log);
    return { from: getAddress(args.from), to: getAddress(args.to), value: args.value, logIndex: log.logIndex! };
  });
}

function oneTransfer(transfers: ReturnType<typeof transferLogs>, from: Address, to: Address) {
  const matches = transfers.filter(item => isAddressEqual(item.from, from) && isAddressEqual(item.to, to));
  if (matches.length !== 1) throw new Error('Exactly one expected token transfer required');
  return matches[0]!;
}

function reconcileTradeLogs(input: {
  family: 'V2' | 'V3';
  route: Awaited<ReturnType<typeof observeExternalRoutePools>>['route'];
  pools: Awaited<ReturnType<typeof observeExternalRoutePools>>['pools'];
  logs: TransactionReceipt['logs'];
  router: Address;
  vault: Address;
  feeRecipient: Address;
  amountIn: bigint;
  feeBps: number;
}) {
  const { route, pools, logs, router, vault, feeRecipient } = input;
  const hopAmounts: Array<{ pool: Address; input: bigint; output: bigint; logIndex: number }> = [];
  for (let index = 0; index < pools.length; index++) {
    const pool = pools[index]!, tokenIn = route.tokens[index]!, tokenOut = route.tokens[index + 1]!;
    const topic = input.family === 'V2' ? v2SwapTopic : v3SwapTopic;
    const entries = logs.filter(log => isAddressEqual(log.address, pool.address) && hexEqual(log.topics[0], topic));
    if (entries.length !== 1) throw new Error('Exactly one selected pool Swap required');
    const log = entries[0]!;
    let amountIn: bigint, amountOut: bigint;
    if (input.family === 'V2') {
      const args = canonicalV2Swap(log);
      const inputIs0 = isAddressEqual(tokenIn, pool.token0), outputIs0 = isAddressEqual(tokenOut, pool.token0);
      amountIn = inputIs0 ? args.amount0In : args.amount1In;
      amountOut = outputIs0 ? args.amount0Out : args.amount1Out;
      if ((inputIs0 ? args.amount1In : args.amount0In) !== 0n || (outputIs0 ? args.amount1Out : args.amount0Out) !== 0n
        || !isAddressEqual(args.sender, router)
        || !isAddressEqual(args.to, index + 1 < pools.length ? pools[index + 1]!.address : router)) throw new Error('V2 route event mismatch');
    } else {
      const args = canonicalV3Swap(log);
      const inputDelta = isAddressEqual(tokenIn, pool.token0) ? args.amount0 : args.amount1;
      const outputDelta = isAddressEqual(tokenOut, pool.token0) ? args.amount0 : args.amount1;
      if (inputDelta <= 0n || outputDelta >= 0n || !isAddressEqual(args.sender, router) || !isAddressEqual(args.recipient, router)) {
        throw new Error('V3 route event mismatch');
      }
      amountIn = inputDelta; amountOut = -outputDelta;
    }
    if (amountIn <= 0n || amountOut <= 0n || (index === 0 ? amountIn !== input.amountIn
      : amountIn !== hopAmounts[index - 1]!.output)) throw new Error('External route amount continuity mismatch');
    hopAmounts.push({ pool: pool.address, input: amountIn, output: amountOut, logIndex: log.logIndex! });
  }
  const firstPool = pools[0]!, lastPool = pools.at(-1)!;
  if (!firstPool || !lastPool || hopAmounts.length !== pools.length) throw new Error('External route has no pools');
  const tokenInTransfers = transferLogs(logs, route.tokens[0]!);
  const tokenOutTransfers = transferLogs(logs, route.tokens.at(-1)!);
  const debit = oneTransfer(tokenInTransfers, vault, firstPool.address);
  if (debit.value !== input.amountIn) throw new Error('Vault input debit mismatch');
  const gross = oneTransfer(tokenOutTransfers, lastPool.address, router);
  if (gross.value !== hopAmounts.at(-1)!.output) throw new Error('Final pool output mismatch');
  let feeCredit: typeof gross, vaultCredit: typeof gross;
  if (isAddressEqual(feeRecipient, vault)) {
    const distributions = tokenOutTransfers.filter(item => isAddressEqual(item.from, router) && isAddressEqual(item.to, vault));
    if (distributions.length !== 2) throw new Error('Fee and vault transfers must remain distinct');
    [feeCredit, vaultCredit] = distributions.sort((a, b) => a.logIndex - b.logIndex) as [typeof gross, typeof gross];
  } else {
    feeCredit = oneTransfer(tokenOutTransfers, router, feeRecipient);
    vaultCredit = oneTransfer(tokenOutTransfers, router, vault);
  }
  const distributed = feeCredit.value + vaultCredit.value;
  if (distributed < gross.value || feeCredit.value !== distributed * BigInt(input.feeBps) / 10_000n) {
    throw new Error('Terminal fee/sweep conservation mismatch');
  }
  const lastSwapIndex = Math.max(...hopAmounts.map(hop => hop.logIndex));
  if (debit.logIndex >= hopAmounts[0]!.logIndex || gross.logIndex >= hopAmounts.at(-1)!.logIndex
    || feeCredit.logIndex <= lastSwapIndex || vaultCredit.logIndex <= feeCredit.logIndex) {
    throw new Error('External trade event order mismatch');
  }
  return { amountIn: debit.value, swapGrossOutput: gross.value, terminalFeeCredit: feeCredit.value,
    vaultNetCredit: vaultCredit.value, preexistingRouterOutputLogResidual: distributed - gross.value,
    hops: hopAmounts };
}

/**
 * Historical V2/V3 ERC20 receipt observation for one reviewed owner operation.
 * It performs no signing, submission, retry, storage mutation, fee settlement or
 * accounting publication. Receipt-time quote validation never renews an expired
 * quote, and Transfer logs remain non-authorizing token-semantics candidates.
 */
async function observeTradeReceiptCore(input: ExternalTradeReceiptInput, nativeGasObservation = false) {
  const runtimeClient = input.client;
  const client: ReceiptClient = { getChainId: runtimeClient.getChainId.bind(runtimeClient), getBlock: runtimeClient.getBlock.bind(runtimeClient),
    getBytecode: runtimeClient.getBytecode.bind(runtimeClient), getStorageAt: runtimeClient.getStorageAt.bind(runtimeClient),
    readContract: runtimeClient.readContract.bind(runtimeClient), getTransaction: runtimeClient.getTransaction.bind(runtimeClient),
    getTransactionReceipt: runtimeClient.getTransactionReceipt.bind(runtimeClient) };
  const history = readTerminalQuoteHistory(structuredClone(input.record));
  const intent = structuredClone(input.intent);
  const reviewed = structuredClone(input.reviewed);
  const identity = structuredClone(input.identity);
  const operation = structuredClone(input.operation);
  const userOperation = structuredClone(input.userOperation);
  const expectedHash = input.expectedHash;
  const budget = { ...input.budget };
  const reported = input.reported === null ? null : structuredClone(input.reported);
  const requiredConfirmations = input.requiredConfirmations;
  const owner = assertInitialCommitment({ history, intent, policy: reviewed.policy, operation, owner: input.owner,
    userOperation, expectedHash, budget });
  const reconciled = await reconcileOwnerOperationReceipt({ client, operation: userOperation, expectedHash, budget,
    reported, requiredConfirmations });
  if (reconciled.status === 'pending' || reconciled.status === 'reorg-uncertain') return { kind: 'complete' as const, value: reconciled };
  const matched = reconciled;

  const [receipt, transaction] = nativeGasObservation ? await Promise.all([
    client.getTransactionReceipt({ hash: reconciled.transactionHash }).then(value => structuredClone(value)),
    client.getTransaction({ hash: reconciled.transactionHash }).then(value => structuredClone(value)),
  ]) : [structuredClone(await client.getTransactionReceipt({ hash: reconciled.transactionHash })),
    structuredClone(await client.getTransaction({ hash: reconciled.transactionHash }))];
  // A transaction may be re-included after the generic observation's canonical
  // head was read. Do not combine that later receipt with the earlier finality
  // fields: a fresh reconciliation is required on the next read instead.
  if (!same(receipt.transactionHash, reconciled.transactionHash)
    || receipt.blockNumber !== reconciled.blockNumber || !same(receipt.blockHash, reconciled.blockHash)
    || !same(transaction.hash, reconciled.transactionHash)
    || transaction.blockNumber !== reconciled.blockNumber || !same(transaction.blockHash, reconciled.blockHash)) {
    return { kind: 'complete' as const, value: { status: 'reorg-uncertain' as const, resubmissionAuthorized: false as const } };
  }
  if (!same(transaction.hash, receipt.transactionHash) || !same(transaction.blockHash, receipt.blockHash)
    || transaction.blockNumber !== receipt.blockNumber || transaction.transactionIndex !== receipt.transactionIndex
    || transaction.chainId !== SELECTED_ACCOUNT_CHAIN_ID || transaction.type !== 'eip1559' || receipt.type !== 'eip1559'
    || transaction.value !== 0n || !transaction.to || !isAddressEqual(transaction.to, pinnedContracts.entryPointV07.address)
    || !receipt.to || !isAddressEqual(receipt.to, pinnedContracts.entryPointV07.address)
    || receipt.contractAddress !== null || !transaction.from || isAddressEqual(transaction.from, zeroAddress)
    || !receipt.from || !isAddressEqual(transaction.from, receipt.from)) {
    throw new Error('External trade bundle transaction mismatch');
  }
  const source = await client.getBlock({ blockNumber: receipt.blockNumber });
  if (source.number !== receipt.blockNumber || !source.hash || !same(source.hash, receipt.blockHash)
    || typeof source.timestamp !== 'bigint' || source.timestamp < 0n
    || !Number.isSafeInteger(receipt.transactionIndex)
    || !hexEqual(source.transactions[receipt.transactionIndex], receipt.transactionHash)) throw new Error('External trade receipt source mismatch');
  const segment = extractSelectedOwnerOperationExecutionLogs({ receipt, transaction, expectedHash, sender: operation.vault, nonce: userOperation.nonce });
  if (segment.userOperationLogIndex !== reconciled.logIndex || segment.actualGasCost !== reconciled.actualGasCost
    || segment.actualGasUsed !== reconciled.actualGasUsed || segment.success !== reconciled.success
    || !hexEqual(segment.selectedCallData, userOperation.callData)) throw new Error('External trade operation segment mismatch');
  await assertActualSignedOperation({ transactionInput: transaction.input, reviewed: userOperation, expectedHash, owner });
  async function finalObservation() {
    // The numbered source fence must follow the completed head read: a reorg
    // during a delayed head response must not publish old-source confirmations.
    const head = structuredClone(await client.getBlock());
    const [recheck, chainId] = nativeGasObservation ? await Promise.all([
      client.getBlock({ blockNumber: source.number }).then(value => structuredClone(value)),
      client.getChainId(),
    ]) : [await client.getBlock({ blockNumber: source.number }), null];
    if (typeof head.number !== 'bigint' || head.number < source.number || !head.hash
      || head.number === source.number && head.hash !== source.hash
      || recheck.number !== source.number || recheck.hash !== source.hash || recheck.timestamp !== source.timestamp
      || (nativeGasObservation ? chainId : await client.getChainId()) !== SELECTED_ACCOUNT_CHAIN_ID)
      return { status: 'reorg-uncertain' as const, resubmissionAuthorized: false as const };
    const confirmations = head.number - source.number + 1n;
    const required = requiredConfirmations ?? 2n;
    return { ...matched,
      status: confirmations < required ? 'confirming' as const : matched.success ? 'confirmed-success' as const : 'confirmed-reverted' as const,
      confirmations, observedHeadNumber: head.number, observedHeadHash: head.hash,
      finality: 'confirmation-observed-not-irreversible' as const,
      executionSource: 'external-terminal' as const, family: history.record.family,
      intentId: intent.intentId, quoteId: history.record.quote.quoteId, operationId: operation.operationId,
      callsHash: operation.callsHash, vault: operation.vault, owner,
      executionAuthorized: false as const, accountingEligible: false as const, feeSettlementAuthorized: false as const,
      resubmissionAuthorized: false as const };
  }
  // A deadline may be exactly why execution reverted. Failure history proves
  // inclusion/gas, not a successful route, so it must not demand fresh swap
  // terms, pool liquidity or token allowances to show that confirmed failure.
  if (!segment.success && !nativeGasObservation) {
    const final = await finalObservation();
    if (final.status === 'reorg-uncertain') return { kind: 'complete' as const, value: final };
    return { kind: 'complete' as const, value: { ...final, outcome: 'reverted' as const, trade: null,
      verification: 'failed-owner-operation-inclusion-only' as const } };
  }
  return { kind: 'success-context' as const, client, history, intent, reviewed, identity, operation, userOperation,
    expectedHash, budget, owner, receipt, transaction, source, segment, finalObservation };
}

/** Historical ERC20 observation. Legacy output semantics are unchanged. */
export async function observeExternalTradeReceipt(input: ExternalTradeReceiptInput) {
  const core = await observeTradeReceiptCore(input);
  if (core.kind === 'complete') return core.value;
  const { client, history, intent, reviewed, identity, operation, source, segment, finalObservation } = core;
  const restored = readTerminalQuoteRecord(history.record, { intent,
    policy: { ...reviewed.policy, nowUnixSeconds: source.timestamp } });
  const swap = restored.decoding.commands.find(command => command.kind === 'SWAP');
  if (!swap || swap.kind !== 'SWAP' || swap.tokenIn === 'NATIVE' || swap.tokenOut === 'NATIVE'
    || !isAddressEqual(operation.vault, swap.swapper) || restored.decoding.callsHash.toLowerCase() !== operation.callsHash.toLowerCase()) {
    throw new Error('Historical external trade quote mismatch');
  }
  const expectedValidUntil = [dateSeconds(intent.expiresAt, 'intent expiry'), dateSeconds(restored.record.quote.expiresAt, 'quote expiry'), swap.deadlineUnixSeconds]
    .reduce((left, right) => left < right ? left : right);
  if (operation.validUntil !== new Date(Number(expectedValidUntil * 1_000n)).toISOString()) throw new Error('External trade review expiry mismatch');
  const route = await observeExternalRoutePools({ client, intent, quote: restored.record.quote, calls: restored.calls,
    policy: { ...reviewed.policy, nowUnixSeconds: source.timestamp }, profile: reviewed.profile,
    source: { number: source.number, hash: source.hash, timestamp: source.timestamp }, now: () => source.timestamp });
  nonzeroAddressSchema.parse(reviewed.permit2.address); bytes32Schema.parse(reviewed.permit2.codeHash);
  const permit2Code = await client.getBytecode({ address: reviewed.permit2.address, blockNumber: source.number });
  if (!permit2Code || permit2Code === '0x' || keccak256(permit2Code).toLowerCase() !== reviewed.permit2.codeHash.toLowerCase()) {
    throw new Error('Historical Permit2 runtime mismatch');
  }
  const vault = await observeVaultIdentityAtBlock({ client, identity, vault: operation.vault,
    source: { number: source.number, hash: source.hash } });
  const final = await finalObservation();
  if (final.status === 'reorg-uncertain') return final;
  const base = { ...final, route, vaultIdentity: vault,
    verification: 'historical-token-semantics-durable-publication-and-finality-required' as const };
  const trade = reconcileTradeLogs({ family: history.record.family, route: route.route, pools: route.pools,
    logs: segment.candidateLogs, router: getAddress(reviewed.policy.router), vault: getAddress(operation.vault),
    feeRecipient: getAddress(reviewed.policy.feeRecipient), amountIn: swap.amountIn, feeBps: swap.feeBps });
  if (trade.vaultNetCredit < BigInt(restored.record.quote.minimumAmountOut)) throw new Error('Observed vault output below reviewed minimum');
  return { ...base, outcome: 'success' as const, trade: { ...trade,
    tokenIn: swap.tokenIn, tokenOut: swap.tokenOut, feeBps: swap.feeBps,
    transferSemanticsVerified: false as const, balanceAccountingPublished: false as const } };
}

export type NativeExternalTradeReceiptInput = ExternalTradeReceiptInput & {
  reviewed: ExternalTradeReceiptInput['reviewed'] & {
    nativeWrapperProfile: NativeWrapperObservationProfile;
    endpointProfile: DirectStorageTokenProfile;
  };
  trace: LocalTracePort;
  statePort: CanonicalReadPort;
  observationPolicy: { maxRequests: number; maxObservationAgeSeconds: number; maxFutureSeconds: number };
  now?: () => number;
};

/** Additive native V2 deployed/first-vault observation. Original schema3 terms and
 * wrapper profile are restored at genuine execution time. Historical state
 * reads use a bounded real collection clock, never a rewound N5 live clock.
 * Failed inclusion restores original terms structurally and observes gas only.
 * This returns no dispatch, financial publication or governance authority. */
export async function observeNativeExternalTradeReceipt(input: NativeExternalTradeReceiptInput) {
  const profile = readNativeWrapperObservationProfile(input.reviewed.nativeWrapperProfile);
  assertNativeWrapperRouterCompatibility(profile, input.reviewed.policy.requiredDecoderVersion);
  const endpointProfile = structuredClone(input.reviewed.endpointProfile);
  const observationPolicy = structuredClone(input.observationPolicy), now = input.now ?? Date.now;
  const trace = input.trace, request = input.statePort.request.bind(input.statePort);
  // The common core captures all remaining review/client fields before awaits.
  const core = await observeTradeReceiptCore(input, true);
  if (core.kind === 'complete') return core.value;
  const { history, intent, reviewed, identity, operation, client, source, receipt, owner, expectedHash, finalObservation } = core;
  if (!isNativeTerminalQuoteRecord(history.record)) throw new Error('Native receipt requires original native record');
  const nativeWrapper = { address: profile.wrapper.account, codeHash: profile.wrapper.codeHash };
  if (!isAddressEqual(profile.router.account, reviewed.policy.router)) throw new Error('Native receipt router profile mismatch');
  const termsTimestamp = core.segment.success ? source.timestamp : [source.timestamp, dateSeconds(operation.validUntil, 'review expiry') - 1n]
    .reduce((a, b) => a < b ? a : b);
  const restored = readTerminalQuoteRecord(history.record, { intent, nativeWrapper,
    policy: { ...reviewed.policy, nowUnixSeconds: termsTimestamp } });
  const swap = restored.decoding.commands.find(command => command.kind === 'SWAP');
  if (!swap || swap.kind !== 'SWAP' || (swap.tokenIn === 'NATIVE') === (swap.tokenOut === 'NATIVE')
    || !isAddressEqual(operation.vault, swap.swapper) || restored.decoding.callsHash.toLowerCase() !== operation.callsHash.toLowerCase()) {
    throw new Error('Historical native trade quote mismatch');
  }
  const until = [dateSeconds(intent.expiresAt, 'intent expiry'), dateSeconds(restored.record.quote.expiresAt, 'quote expiry'), swap.deadlineUnixSeconds]
    .reduce((left, right) => left < right ? left : right);
  if (operation.validUntil !== new Date(Number(until * 1000n)).toISOString()) throw new Error('External trade review expiry mismatch');
  const historical = await createHistoricalCanonicalBlockReadClient({ port: { request },
    source: { number: source.number, hash: source.hash, timestamp: source.timestamp }, policy: observationPolicy, now,
    parallelSourceChecks: true });
  // Both reads are bound to the captured historical source/server pins. Permit2
  // does not depend on the observed route; both must finish before trace work.
  const [route, permit2Code] = core.segment.success ? await Promise.all([
    observeExternalRoutePools({ client: historical.client, intent, quote: restored.record.quote,
      calls: restored.calls, policy: { ...reviewed.policy, nowUnixSeconds: source.timestamp }, profile: reviewed.profile,
      nativeWrapper, source: historical.source, now: () => source.timestamp }),
    historical.client.getBytecode({ address: reviewed.permit2.address, blockNumber: source.number }),
  ]) : [null, undefined] as const;
  if (route && route.pools.length !== 1) throw new Error('Native receipt requires one V2 pool');
  if (route) {
    if (!permit2Code || permit2Code === '0x' || keccak256(permit2Code).toLowerCase() !== reviewed.permit2.codeHash.toLowerCase()) throw new Error('Historical Permit2 runtime mismatch');
  }
  const provenance = createSmaBVaultIntent(identity);
  const cashflow = await collectSelectedNativeTerminalSettlementTrace({ client, stateClient: historical.client, local: trace,
    transactionHash: receipt.transactionHash, owner: { vault: operation.vault, owner, originalOwner: provenance.owner,
      userOperationHash: expectedHash, nonceLane: operation.nonceKey, factory: provenance.factory,
      implementation: provenance.implementation, salt: BigInt(provenance.salt), callDataHash: keccak256(core.userOperation.callData) },
    nativeTrade: { intent, quote: restored.record.quote, calls: restored.calls,
      policy: { ...reviewed.policy, nowUnixSeconds: source.timestamp }, nativeWrapper: profile.wrapper.account },
    wrapperProfile: profile, endpointProfile, pool: route?.pools[0] });
  // These fences run AFTER all added awaits, including debug/state observations.
  await historical.verifySource();
  const final = await finalObservation();
  if (final.status === 'reorg-uncertain') return final;
  // The actual bound validation prestate/signature, not a block-end signer,
  // identifies this execution. A later same-block rotation cannot rewrite it.
  const validation = cashflow.owner.evidence.validation;
  const vaultIdentity = { kind: 'sma-b-transaction-execution-identity-observation' as const, chainId: SELECTED_ACCOUNT_CHAIN_ID,
    vault: operation.vault, originalOwner: validation.ownerResolution.originalOwner, ownerAtExecution: validation.owner,
    vaultRuntimeHash: validation.implementation.runtimeHash, ownerResolution: validation.ownerResolution,
    source: cashflow.owner.evidence.source, registryPromotionAuthorized: false as const };
  return { ...final, route, vaultIdentity, nativeCashflow: cashflow,
    verification: 'historical-native-cashflows-not-publication-or-finality-proof' as const,
    outcome: cashflow.outcome === 'unavailable' ? 'unavailable' as const : cashflow.outcome === 'reverted' ? 'reverted' as const : 'success' as const,
    reason: cashflow.reason, trade: cashflow.trade, submissionAuthorized: false as const,
    upgradeAuthorityVerified: false as const, balanceAccountingPublished: false as const };
}
